/**
 * CSV Parser Utility
 * 
 * Handles parsing of CSV and Excel files for Crown Jewels import
 * Uses papaparse for CSV files and xlsx for Excel files
 */

import Papa from 'papaparse';
import * as XLSX from 'xlsx';

/**
 * Parse CSV file and return structured data
 * @param {File} file - The uploaded CSV file
 * @returns {Promise} Promise resolving to parsed data
 */
export const parseCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true, // Use first row as column headers
      skipEmptyLines: true,
      transformHeader: (header) => {
        // Clean up header names by trimming whitespace
        return header.trim();
      },
      complete: (results) => {
        if (results.errors.length > 0) {
          console.warn('CSV parsing warnings:', results.errors);
        }
        
        resolve({
          data: results.data,
          headers: results.meta.fields || [],
          errors: results.errors,
          meta: results.meta
        });
      },
      error: (error) => {
        reject(new Error(`CSV parsing failed: ${error.message}`));
      }
    });
  });
};

/**
 * Parse Excel file and return structured data
 * @param {File} file - The uploaded Excel file
 * @returns {Promise} Promise resolving to parsed data
 */
export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Use the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON with header row as keys
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1, // Get raw data first
          defval: '' // Default value for empty cells
        });
        
        if (jsonData.length === 0) {
          reject(new Error('Excel file appears to be empty'));
          return;
        }
        
        // Extract headers from first row
        const headers = jsonData[0].map(header => 
          typeof header === 'string' ? header.trim() : String(header).trim()
        );
        
        // Convert remaining rows to objects using headers
        const dataRows = jsonData.slice(1).map(row => {
          const rowObj = {};
          headers.forEach((header, index) => {
            const value = row[index];
            // Convert values to strings and handle different data types
            rowObj[header] = value !== undefined && value !== null 
              ? String(value).trim() 
              : '';
          });
          return rowObj;
        }).filter(row => {
          // Filter out completely empty rows
          return Object.values(row).some(value => value !== '');
        });
        
        resolve({
          data: dataRows,
          headers: headers,
          errors: [],
          meta: {
            fields: headers,
            delimiter: ',', // Not applicable for Excel but included for consistency
            linebreak: '\n'
          }
        });
      } catch (error) {
        reject(new Error(`Excel parsing failed: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Auto-detect file type and parse accordingly
 * @param {File} file - The uploaded file
 * @returns {Promise} Promise resolving to parsed data
 */
export const parseFile = async (file) => {
  const fileName = file.name.toLowerCase();
  const fileType = getFileType(fileName);
  
  switch (fileType) {
    case 'csv':
      return await parseCSVFile(file);
    case 'excel':
      return await parseExcelFile(file);
    default:
      throw new Error(`Unsupported file type: ${fileName}`);
  }
};

/**
 * Determine file type from filename
 * @param {string} fileName - The filename to check
 * @returns {string} File type ('csv', 'excel', or 'unknown')
 */
export const getFileType = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  
  if (extension === 'csv') {
    return 'csv';
  } else if (['xlsx', 'xls'].includes(extension)) {
    return 'excel';
  } else {
    return 'unknown';
  }
};

/**
 * Validate file before parsing
 * @param {File} file - The file to validate
 * @returns {object} Validation result with isValid and errors
 */
export const validateFile = (file) => {
  const errors = [];
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  
  // Check file size
  if (file.size > maxSize) {
    errors.push('File size exceeds 10MB limit');
  }
  
  // Check file type
  const fileType = getFileType(file.name.toLowerCase());
  if (fileType === 'unknown') {
    errors.push('Unsupported file type. Please upload CSV or Excel (.xlsx, .xls) files only');
  }
  
  // Check if file is empty
  if (file.size === 0) {
    errors.push('File is empty');
  }
  
  return {
    isValid: errors.length === 0,
    errors: errors,
    fileType: fileType
  };
};

/**
 * Get a preview of parsed data (first 5 rows)
 * @param {Array} data - Full parsed data array
 * @returns {Array} First 5 rows of data
 */
export const getDataPreview = (data) => {
  return data.slice(0, 5);
};

/**
 * Detect delimiter in CSV content
 * @param {string} csvContent - Raw CSV content
 * @returns {string} Detected delimiter
 */
export const detectDelimiter = (csvContent) => {
  const delimiters = [',', ';', '\t', '|'];
  const lines = csvContent.split('\n').slice(0, 5); // Check first 5 lines
  
  let bestDelimiter = ',';
  let maxCount = 0;
  
  delimiters.forEach(delimiter => {
    const counts = lines.map(line => (line.split(delimiter).length - 1));
    const avgCount = counts.reduce((sum, count) => sum + count, 0) / counts.length;
    const consistency = counts.every(count => Math.abs(count - avgCount) <= 1);
    
    if (avgCount > maxCount && consistency && avgCount > 1) {
      maxCount = avgCount;
      bestDelimiter = delimiter;
    }
  });
  
  return bestDelimiter;
}; 